import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  Calendar,
  Hash,
  Type,
  MapPin,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function toISODateInput(d) {
  // yyyy-mm-dd for <input type="date">
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const CLAAllowanceReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // from login
  const branchType = localStorage.getItem("branchType") || "HO"; // "HO" or other

  const screenBadge = useMemo(() => "INFRA SCREEN : C-62", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // meta
  const [branches, setBranches] = useState([]);
  const [deptNames, setDeptNames] = useState([]);
  const [deptCodes, setDeptCodes] = useState([]);

  // filters
  const [showFilter, setShowFilter] = useState(true);
  const [branchId, setBranchId] = useState("Select");
  const [deptName, setDeptName] = useState("Select");
  const [deptCode, setDeptCode] = useState("Select");
  const [fromDate, setFromDate] = useState(""); // yyyy-mm-dd
  const [toDate, setToDate] = useState(""); // yyyy-mm-dd

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // totals
  const [grandTotal, setGrandTotal] = useState(0);

  const canViewAll = branchType === "HO";

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/cla-allowance/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId || 0),
          branchType: branchType || "",
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setDeptNames(res.data.deptNames || []);
      setDeptCodes(res.data.deptCodes || []);

      // default dates -> current month range
      const defFrom = toISODateInput(res.data.defaultFromDate);
      const defTo = toISODateInput(res.data.defaultToDate);

      setFromDate(defFrom);
      setToDate(defTo);

      // default branch -> login branch
      const defBranch = res.data.defaultBranchId ? String(res.data.defaultBranchId) : "Select";
      setBranchId(defBranch);

      // default selects
      setDeptName("Select");
      setDeptCode("Select");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    // ASP: if branchtype HO, branch is selectable, otherwise fixed.
    // In our UI, for non-HO we keep it fixed but still validate.
    if (!String(branchId).trim() || String(branchId) === "Select") {
      toast.warn("Please Select Branch..");
      return;
    }

    // ASP: if one date missing -> "Select Date"
    const hasFrom = !!String(fromDate).trim();
    const hasTo = !!String(toDate).trim();
    if ((hasFrom && !hasTo) || (!hasFrom && hasTo)) {
      toast.warn("Select Date");
      return;
    }

    // ASP: if both empty -> defaults; but we always have defaults in meta,
    // still keep safe fallback:
    const sendFrom = hasFrom ? fromDate : "";
    const sendTo = hasTo ? toDate : "";

    const deptNameParam = deptName !== "Select" ? deptName : "";
    const deptCodeParam = deptCode !== "Select" ? deptCode : "";

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/cla-allowance/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          deptName: deptNameParam,
          deptCode: deptCodeParam,
          fromDate: sendFrom,
          toDate: sendTo,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setGrandTotal(Number(res.data.grandTotal || 0));
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pagination fetch
  useEffect(() => {
    if (page >= 1) fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    // ASP ClearContents: clears dates + depts (and keeps branch)
    setDeptName("Select");
    setDeptCode("Select");
    setFromDate("");
    setToDate("");

    // fetchList will apply default month range when both empty (Node does it)
    setPage(1);
    await fetchList(1);
  };

  const onChangeDeptName = (v) => {
    setDeptName(v);
    if (v !== "Select") setDeptCode("Select"); // mutual reset like ASP
  };

  const onChangeDeptCode = (v) => {
    setDeptCode(v);
    if (v !== "Select") setDeptName("Select"); // mutual reset like ASP
  };

// ✅ Type=6 print (CLA Report - All)  -> /PayRoll/CLAReportPrintType6
// ✅ Type=6 print (CLA Report - All) -> /PayRoll/CLAReportPrintType6
const handlePrintType6 = () => {
  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }

  const hasFrom = !!String(fromDate || "").trim();
  const hasTo = !!String(toDate || "").trim();

  if ((hasFrom && !hasTo) || (!hasFrom && hasTo)) {
    toast.warn("Select Date (both FromDate and ToDate required)");
    return;
  }

  if (!String(branchId).trim() || String(branchId) === "Select") {
    toast.warn("Please Select Branch..");
    return;
  }

  // Find branch name from branches array
  const selectedBranch = branches.find(b => String(b.id) === String(branchId));
  const branchName = selectedBranch?.name || "";

  const params = new URLSearchParams();
  params.set("finYearId", String(finYearId));
  params.set("branchId", String(branchId));
  
  if (branchName) {
    params.set("branchName", branchName);
  }

  if (hasFrom && hasTo) {
    // Pass dates in YYYY-MM-DD format (not ISO string)
    params.set("fromDate", fromDate);
    params.set("toDate", toDate);
  }

  // ✅ CORRECT URL - should match your route configuration
  const url = `/PayRoll/CLAReportPrintType6?${params.toString()}`;
  window.open(url, "_blank", "noopener,noreferrer");
};


const handlePrintType4 = () => {
  if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
  if (!fromDate || !toDate) return toast.warn("Select Date (both FromDate and ToDate required)");
  if (!branchId || String(branchId) === "Select") return toast.warn("Please Select Branch..");

  const url =
    `/PayRoll/CLAAllowanceReport_Print_Type4?finYearId=${encodeURIComponent(finYearId)}` +
    `&branchId=${encodeURIComponent(branchId)}` +
    `&deptName=${encodeURIComponent(deptName || "")}` +
    `&deptCode=${encodeURIComponent(deptCode || "")}` +
    `&fromDate=${encodeURIComponent(fromDate)}` +
    `&toDate=${encodeURIComponent(toDate)}`;

  window.open(url, "_blank", "noopener,noreferrer");
};

const handleDepartmentClick = (r) => {
  if (!r) return;

  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }

  if (!String(branchId).trim() || String(branchId) === "Select") {
    toast.warn("Please Select Branch..");
    return;
  }

  if (!fromDate || !toDate) {
    toast.warn("Select Date (both FromDate and ToDate required)");
    return;
  }

  const depId = r?.fld_deptid || r?.deptId || "";
  const depName = r?.fld_departmentname || r?.Department || "";
  const depCode = r?.fld_deptcode || r?.deptCode || "";

  if (!depId) {
    toast.warn("Department ID not found.");
    return;
  }

  const url =
    `/PayRoll/CLAAllowanceReport_Print_Type5?finYearId=${encodeURIComponent(finYearId)}` +
    `&branchId=${encodeURIComponent(branchId)}` +
    `&depId=${encodeURIComponent(depId)}` +
    `&depName=${encodeURIComponent(depName)}` +
    `&depCode=${encodeURIComponent(depCode)}` +
    `&fromDate=${encodeURIComponent(String(fromDate).slice(0, 10))}` +
    `&toDate=${encodeURIComponent(String(toDate).slice(0, 10))}`;

  window.open(url, "_blank", "noopener,noreferrer");
};

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CLA ALLOWANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  CLA Allowance Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter • Search • Pagination • Grand Total
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                View All
              </button>

              <button onClick={handlePrintType6} className={ghostBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              {/* <button onClick={handlePrintType4} className={ghostBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print Supplier
              </button> */}

              <button onClick={() => setShowFilter((v) => !v)} className={ghostBtn}>
                {showFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilter ? "Hide Filter" : "Show Filter"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilter && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* From date */}
                <div>
                  <label className={labelBase}>From Date</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* To date */}
                <div>
                  <label className={labelBase}>To Date</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* Dept name */}
                <div>
                  <label className={labelBase}>Department Name</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={deptName} onChange={(e) => onChangeDeptName(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      {deptNames.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Selecting Department Name resets Department Code (ASP behavior)
                  </div>
                </div>

                {/* Dept code */}
                <div>
                  <label className={labelBase}>Department Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={deptCode} onChange={(e) => onChangeDeptCode(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      {deptCodes.map((d) => (
                        <option key={d.id} value={d.code}>
                          {d.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Selecting Department Code resets Department Name (ASP behavior)
                  </div>
                </div>

                {/* Branch */}
                <div className="md:col-span-2">
                  <label className={labelBase}>Branch</label>
                  <div className="mt-2 flex items-center gap-2">
                    <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select
                      value={branchId}
                      onChange={(e) => setBranchId(e.target.value)}
                      className={inputBase}
                      disabled={!canViewAll}
                      title={!canViewAll ? "Branch is locked for non-HO users" : ""}
                    >
                      <option value="Select">Select</option>
                      {branches.map((b) => (
                        <option key={b.id} value={String(b.id)}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!canViewAll && (
                    <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Branch is locked because your branch type is not HO
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-center gap-3">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Department Code</th>
                    <th className="px-4 py-4 text-left font-bold">Department Name</th>
                    <th className="px-4 py-4 text-right font-bold">Total Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 4 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_deptcode}
                        </td>

                        <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          <button
                            type="button"
                            onClick={() => handleDepartmentClick(r)}
                            className={`font-bold underline ${isDark ? "text-blue-300" : "text-blue-700"}`}
                            title="Click to drilldown (Type=5) - will be enabled after print implementation"
                          >
                            {r.fld_departmentname}
                          </button>
                        </td>

                        <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_totalamount}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Footer grand total like ASP */}
                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td colSpan={3} className={`px-4 py-4 font-extrabold text-right ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                        Grand Total :
                      </td>
                      <td className={`px-4 py-4 font-extrabold text-right ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                        {Number(grandTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default CLAAllowanceReport;
